import { useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { format } from 'date-fns'
import { AppDispatch, RootState } from 'store'
import { NoItems } from 'components/NoItems-fallback/NoItems'
import { Pagination } from 'components/Pagination/Pagination'
import { HonestTable, IHonestTableColumn } from 'components/HonestTable/HonestTable'
import { Options } from 'components/Options/Options'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { Button } from 'components/ui/Button/Button'
import {
  approveInventoryAssignment,
  declineInventoryAssignment,
} from 'features/inventory/inventoryItemsSlice'
import { setConfirmModal } from 'features/global/globalSlice'
import { ModalTypes } from 'types/global.interface'
import { IGetAllInventoryAssignmentsSingleResponse } from 'types/api/inventoryItems.interface'
import { IInventoryCustodyResponse } from 'types/api/inventoryControlApi.interface'
import { IStoreCustodyRow, IStoreOutstandingTabProps } from 'types/inventoryCounter.interface'
import { InventoryCustodyStatus } from 'types/inventoryEnums.interface'
import { Flex, PageFooter } from 'styling/GlobalStyles'
import { OtherText } from 'styling/Typography'
import { formatDateForDisplay, handlePagination } from 'utils/helpers'
import { inventoryAssignmentStatuses } from '../Assignments/InventoryAssignments'
import { ViewInventoryAssignmentModal } from '../Assignments/ViewInventoryAssignmentModal'
import { CUSTODY_STATUS_META, CUSTODY_UI_STATUS_META } from '../Shared/inventoryEnumMaps'
import { OUTSTANDING_CAPTION } from '../Shared/inventoryFormatters'
import { CustodyItemText, LedgerMetaText } from './StoreCounter.styled'

const mapCustodyToRow = (row: IInventoryCustodyResponse): IStoreCustodyRow => {
  const meta = CUSTODY_STATUS_META[row.status]
  const uiKey =
    row.status === InventoryCustodyStatus.Overdue
      ? 'OVERDUE'
      : row.status === InventoryCustodyStatus.Returned
        ? 'RETURNED'
        : 'OK'
  return {
    id: String(row.id),
    item: row.item?.name ?? 'Item',
    holder: row.holder?.name ?? '—',
    dueLabel: row.dueBackAt
      ? `Due back ${format(new Date(row.dueBackAt), 'EEE d MMM')}`
      : '—',
    status: uiKey,
    pillLabel: meta?.label ?? uiKey,
  }
}

export const OutstandingTab = ({
  params,
  setParams,
  localCustody,
  onProcessReturn,
  onProcessReturnAssignment,
}: IStoreOutstandingTabProps) => {
  const { assignments } = useSelector((store: RootState) => store.inventoryItems)
  const { custody } = useSelector((store: RootState) => store.inventoryControl)
  const dispatch = useDispatch<AppDispatch>()
  const [viewModal, setViewModal] = useState<{
    open: boolean
    value: IGetAllInventoryAssignmentsSingleResponse
  }>({ open: false, value: null })

  const handleRequest = (
    id: number,
    approve: boolean,
    item?: IGetAllInventoryAssignmentsSingleResponse,
  ) => {
    if (item) {
      setViewModal({ open: true, value: item })
      return
    }
    dispatch(
      setConfirmModal({
        isOpen: true,
        onSave: async () => {
          await dispatch(approve ? approveInventoryAssignment(id) : declineInventoryAssignment(id))
        },
        setParams: {
          type: ModalTypes.BeforeSabe,
          f: setParams,
          noItems: assignments.data.length < 2,
        },
        word: approve ? 'approve this assignment' : 'decline this assignment',
        }),
    )
  }

  const columns: IHonestTableColumn<any>[] = [
    { id: 'type', header: 'TYPE', cell: (item) => (item.type == 1 ? 'Assign' : 'Unassign') },
    { id: 'location', header: 'LOCATION', cell: (item) => item.location?.name || '/' },
    {
      id: 'status',
      header: 'STATUS',
      cell: (item) => (
        <StatusPill
          status={
            item.status == 1
              ? 'warning'
              : item.status == 2
                ? 'success'
                : item.status == 3
                  ? 'danger'
                  : 'neutral'
          }
        >
          {inventoryAssignmentStatuses.find((s) => s.value == item.status)?.label || '/'}
        </StatusPill>
      ),
    },
    {
      id: 'holder',
      header: 'HOLDER',
      cell: (item) =>
        item.assignee
          ? `${item.assignee.firstName} ${item.assignee.lastName}`
          : <OtherText grey>—</OtherText>,
    },
    {
      id: 'due',
      header: 'DUE BACK',
      cell: (item) =>
        item.dueBackAt ? formatDateForDisplay(item.dueBackAt) : <OtherText grey>—</OtherText>,
    },
    { id: 'date', header: 'DATE', cell: (item) => formatDateForDisplay(item.createdAt) },
    { id: 'items', header: 'ITEMS', cell: (item) => item.lines?.length || '0' },
    {
      id: 'actions',
      header: '',
      align: 'right',
      cell: (item) => (
        <span onClick={(e) => e.stopPropagation()}>
          <Flex center gap='0.5rem'>
            {item.type == 1 && item.status == 2 && (
              <Button variant='primary' size='sm' onClick={() => onProcessReturnAssignment?.(item)}>
                Process return
              </Button>
            )}
            <Options item={item} title='Assignments' openModal={handleRequest} />
          </Flex>
        </span>
      ),
    },
  ]

  const apiCustodyRows = useMemo(
    () => (custody.data ?? []).map(mapCustodyToRow),
    [custody.data],
  )
  const custodyRows: IStoreCustodyRow[] = [...localCustody, ...apiCustodyRows]
  const showCustody = custodyRows.length > 0 && !assignments?.data?.length

  const custodyColumns: IHonestTableColumn<IStoreCustodyRow>[] = [
    {
      id: 'item',
      header: 'ITEM',
      minWidth: '16rem',
      cell: (row) => <CustodyItemText>{row.item}</CustodyItemText>,
    },
    { id: 'holder', header: 'HOLDER', cell: (row) => row.holder },
    {
      id: 'due',
      header: 'DUE BACK',
      cell: (row) => <LedgerMetaText>{row.dueLabel}</LedgerMetaText>,
    },
    {
      id: 'status',
      header: 'STATUS',
      cell: (row) => {
        const meta = CUSTODY_UI_STATUS_META[row.status]
        return (
          <StatusPill status={meta?.tone ?? 'neutral'} dot={meta?.dot}>
            {row.pillLabel}
          </StatusPill>
        )
      },
    },
    {
      id: 'actions',
      header: '',
      align: 'right',
      cell: (row) =>
        row.status !== 'RETURNED' ? (
          <Flex center gap='0.5rem'>
            {row.status === 'OVERDUE' && (
              <Button
                variant='secondary'
                size='sm'
                onClick={() =>
                  toast.info(`Return requested — ${row.holder} notified about ${row.item}`)
                }
              >
                Request return
              </Button>
            )}
            <Button variant='primary' size='sm' onClick={() => onProcessReturn?.(row)}>
              Process return
            </Button>
          </Flex>
        ) : null,
    },
  ]

  return (
    <>
      <Flex mb='16'>
        <OtherText grey>{OUTSTANDING_CAPTION}</OtherText>
      </Flex>
      {assignments?.data?.length ? (
        <>
          <HonestTable
            columns={columns}
            data={assignments.data}
            keyField='id'
            onClick={(item) => handleRequest(item.id, false, item)}
          />
          <PageFooter>
            <Pagination
              setPage={(page) => handlePagination(page, setParams, 30)}
              pageCount={Math.ceil(assignments?.totalPages)}
              forcePage={(params.page ? params.page : 1) - 1}
            />
          </PageFooter>
        </>
      ) : showCustody ? (
        <>
          <HonestTable columns={custodyColumns} data={custodyRows} keyField='id' />
          {custody.totalPages > 1 && (
            <PageFooter>
              <Pagination
                setPage={(page) => handlePagination(page, setParams, 30)}
                pageCount={Math.ceil(custody.totalPages)}
                forcePage={(params.page ? params.page : 1) - 1}
              />
            </PageFooter>
          )}
        </>
      ) : (
        <NoItems title='Outstanding custody' />
      )}
      {viewModal.open && (
        <ViewInventoryAssignmentModal
          value={viewModal.value}
          closeModal={() => setViewModal({ value: null, open: false })}
        />
      )}
    </>
  )
}
